/**
 * Shared circle geometry + the Terra Draw render-mode constant for annotations
 * (ADR-034; feature-spec §5.2 / D12).
 *
 * :func:`circleRing` turns a circle's canonical centre ``Point`` + ``radius_m``
 * into a closed geodesic ``Polygon`` ring. ADR-034 PR D (#429) moved
 * persisted-annotation *display* off Terra Draw's RenderMode onto Meridian-owned
 * MapLibre layers, so the ring generator is now consumed by the Meridian render
 * path (``@/annotations/annotation-map-features``). It stays pure + dependency-
 * free (no map / no GL), so it is unit-testable in CI.
 *
 * ``ANNOTATION_RENDER_MODE`` is the Terra Draw idle render-mode name consumed by
 * :func:`map/use-annotation-draw.useAnnotationDraw`.
 *
 * Cross-references:
 *   - ADR-034; feature-spec §5.2 (UI architecture) / D12 (Terra Draw rendering).
 */

/** The Terra Draw idle render-mode name (consumed by use-annotation-draw). */
export const ANNOTATION_RENDER_MODE = "render";

/** Default segment count for a rendered circle ring. 256 keeps the outline
 *  smooth even for large / zoomed-in circles — a meter-radius circle is a
 *  polygon approximation (MapLibre has no native geo-circle), so the facet
 *  count is what governs smoothness. */
export const CIRCLE_RING_STEPS = 256;

/** Mean Earth radius in metres (WGS84 sphere approximation). */
const EARTH_RADIUS_M = 6_371_008.8;

/** Terra Draw's default coordinate precision (decimal places). Generated ring
 *  coordinates MUST be limited to this — full double precision trips Terra
 *  Draw's "Feature has coordinates with excessive precision" validation on
 *  addFeatures (drawn shapes are precision-limited; a generated circle is not),
 *  which is why drawn circles never rendered (ADR-034 bug fix). */
const COORD_PRECISION = 9;

/** Round a coordinate to Terra Draw's coordinate precision. */
function roundCoord(value: number): number {
  return Number(value.toFixed(COORD_PRECISION));
}

/** Due-east bearing in radians — the direction of the radius line (REQ-UI-134). */
const BEARING_EAST = Math.PI / 2;

/**
 * The position ``radiusM`` metres from ``[centerLon, centerLat]`` along
 * ``bearingRad`` (clockwise from north), by spherical destination-point math.
 *
 * Shared by :func:`circleRing` and :func:`circleRadiusLine` so the radius line
 * terminates **exactly** on the rendered ring rather than merely near it — a
 * radius line that stops short or overshoots the edge reads as a rendering bug.
 */
function destinationPoint(
  centerLon: number,
  centerLat: number,
  radiusM: number,
  bearingRad: number,
): number[] {
  const latRad = (centerLat * Math.PI) / 180;
  const lonRad = (centerLon * Math.PI) / 180;
  const angular = radiusM / EARTH_RADIUS_M; // angular distance (radians)
  const lat2 = Math.asin(
    Math.sin(latRad) * Math.cos(angular) +
      Math.cos(latRad) * Math.sin(angular) * Math.cos(bearingRad),
  );
  const lon2 =
    lonRad +
    Math.atan2(
      Math.sin(bearingRad) * Math.sin(angular) * Math.cos(latRad),
      Math.cos(angular) - Math.sin(latRad) * Math.sin(lat2),
    );
  return [roundCoord((lon2 * 180) / Math.PI), roundCoord((lat2 * 180) / Math.PI)];
}

/**
 * A closed geodesic ring approximating a circle of ``radiusM`` metres around
 * ``[centerLon, centerLat]`` (WGS84 degrees). Returns ``steps + 1`` positions
 * with the last identical to the first (a valid GeoJSON linear ring). Pure
 * spherical destination-point math — no dependency.
 */
export function circleRing(
  centerLon: number,
  centerLat: number,
  radiusM: number,
  steps: number = CIRCLE_RING_STEPS,
): number[][] {
  const ring: number[][] = [];
  for (let i = 0; i <= steps; i += 1) {
    // i === steps reuses bearing 0 so the ring closes exactly on its start.
    // Negative bearing winds the ring counter-clockwise (N→W→S→E) — the GeoJSON
    // right-hand rule for an exterior ring.
    const bearing = (-2 * Math.PI * (i % steps)) / steps;
    ring.push(destinationPoint(centerLon, centerLat, radiusM, bearing));
  }
  return ring;
}

/**
 * The circle's radius line: a two-position segment from the centre to the
 * **due-east** point on the ring (REQ-UI-134). Rendered dotted, with the radius
 * measurement labelled at its midpoint, so an operator can read a circle's size
 * off the map without selecting it.
 *
 * Due east is what makes it read as horizontal. It is a *geodesic* east rather
 * than a constant-latitude east, so the endpoint drifts fractionally poleward —
 * but that drift is ~1e-3 degrees even at a 50 km radius, orders of magnitude
 * below a pixel at any usable zoom, and it buys the guarantee that the line ends
 * exactly on the ring (``CIRCLE_RING_STEPS`` is divisible by 4, so the due-east
 * bearing coincides with a real ring vertex).
 *
 * The outer end doubles as the circle's **resize handle** under the Edit tool.
 */
export function circleRadiusLine(
  centerLon: number,
  centerLat: number,
  radiusM: number,
): [number[], number[]] {
  return [[centerLon, centerLat], destinationPoint(centerLon, centerLat, radiusM, BEARING_EAST)];
}

/**
 * The midpoint of the radius line — where the radius measurement label anchors
 * so the text sits *on* the line rather than colliding with the centre marker
 * or the ring.
 */
export function circleRadiusMidpoint(
  centerLon: number,
  centerLat: number,
  radiusM: number,
): number[] {
  const [start, end] = circleRadiusLine(centerLon, centerLat, radiusM);
  return [roundCoord((start[0] + end[0]) / 2), roundCoord((start[1] + end[1]) / 2)];
}
